import { Box } from "@smile2impress/components/relaunch";
import {
    CmsPagePreviewStatus,
    CmsPagesProps,
    CmsStaticProps,
    getPages,
    getPagesLocalizations,
} from "api/cms-website";
import { getProducts } from "api/prices";

import { I18nLocale, I18nToStrapiLocale } from "constants/enum";
import {
    buildDedupedCmsCatchAllPaths,
    getCmsPageCommonProps,
    getCmsPageLayoutProps,
    getSlugStringFromParams,
    normalizeCmsProducts,
} from "utils/cmsPageUtils";
import { CmsComponentMatcher } from "utils/cmsComponentMatcher";
import { reportToSentry } from "utils/reportToSentry";
import { buildCmsPageHreflangAlternates } from "utils/cmsHreflang";
import { getLocations } from "utils/location";
import { getUTSLocale } from "utils/locale";
import { cmsComponentNamespaceMatcher } from "utils/cmsComponentNamespaceMatcher";
import { cmsComponentWithLocationsMatcher } from "utils/cmsComponentWithLocationsMatcher";

export default function CmsPages({
    components,
    locations,
    products,
}: CmsPagesProps) {
    if (components.length < 1) {
        return null;
    }

    return (
        <Box>
            {components.map((component, index) => (
                <Box key={`${component.__component}-${component.id}-${index}`}>
                    {CmsComponentMatcher(component, { locations, products })}
                </Box>
            ))}
        </Box>
    );
}

export async function getStaticPaths() {
    try {
        const data = await getPagesLocalizations();
        const paths = buildDedupedCmsCatchAllPaths(data?.data ?? []);

        return {
            paths,
            // CMS paths come from Strapi at build (PagesApi.findAll per locale). Use fallback:
            // false — only prebuilt paths (new CMS pages after deploy need a rebuild).
            // "blocking" — allow generating unknown paths on first request (ISR-like).
            fallback: false,
        };
    } catch (e) {
        const error = e as Error;
        reportToSentry({ error, extra: { ...error } });
        throw e;
    }
}

export async function getStaticProps({
    params,
    locale = I18nLocale.SPAIN_ES,
}: CmsStaticProps) {
    const slugString = getSlugStringFromParams(params);

    try {
        const localeToLanguage = I18nToStrapiLocale[locale];

        const data = await getPages({
            slugString,
            localeToLanguage,
            previewStatus: CmsPagePreviewStatus.PUBLISHED,
        });

        if (!data?.data?.length) {
            return { notFound: true };
        }

        const utsLocale = getUTSLocale(locale);
        const [locations, products] = await Promise.all([
            utsLocale ? getLocations(utsLocale) : Promise.resolve(null),
            getProducts(),
        ]);
        const content = data.data[0];

        const hreflangAlternates = buildCmsPageHreflangAlternates(
            content,
            locale,
            slugString
        );

        const components =
            content?.components?.map((component) => component?.selected?.[0]) ||
            [];
        const componentNamespaces = cmsComponentNamespaceMatcher(components);
        const withLocations = cmsComponentWithLocationsMatcher(components);

        const commonProps = await getCmsPageCommonProps(locale, {
            additionalNamespaces: componentNamespaces,
        });

        return {
            props: {
                ...commonProps,
                ...getCmsPageLayoutProps(content),
                components,
                locations: withLocations ? locations : null,
                products: normalizeCmsProducts(products, locale),
                hreflangAlternates,
            },
        };
    } catch (e) {
        const error = e as Error;
        reportToSentry({ error, extra: { ...error } });
        throw e;
    }
}
